<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { svgIcon, baseInput } from "@/common/components";

import { methodGroupList } from "@/common/api/rest/method/method-group";
import { methodItemList } from "@/common/api/rest/method/method-item";

import type { MethodGroupType } from "@/common/types/method/method-group";
import type { MethodItemType } from "@/common/types/method/method-item";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = defineProps<{
  title?: string;
  accountId?: string | null;
  categoryId?: string | null;
  ids?: string[];
}>();

const inputSearch = ref<string | null>("");
const groups = ref<MethodGroupType[]>([]);
const methods = ref<MethodItemType[]>([]);

const selectedGroupId = ref<string | null>(null);
const selectedMethod = ref<MethodItemType | null>(null);

function fetchCategory() {
  if (!props.accountId) return;
  methodGroupList(props.accountId, {
    all: true,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        groups.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function search() {
  if (!props.accountId || !selectedGroupId.value) return;

  methodItemList(props.accountId, selectedGroupId.value, {
    search: inputSearch.value as string,
    all: true,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        methods.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function selectGroup(data: MethodGroupType) {
  selectedGroupId.value = data.group_id;
  search();
}

function selectMethod(data: MethodItemType) {
  if (props.ids?.includes(data.method_id)) return;

  selectedMethod.value = data;
  emit("update-data", data);
}

onMounted(() => {
  if (!props.categoryId) fetchCategory();
  else selectedGroupId.value = props.categoryId;
});
</script>

<template>
  <section class="base-search">
    <div class="title">
      {{ title }}
    </div>

    <template v-if="!selectedGroupId">
      <div class="group">
        <div class="label">Selecione o Grupo de Métodos:</div>
        <div class="list">
          <template v-for="item in groups" :key="item.group_id">
            <div class="item" @click="selectGroup(item)">
              <div class="name">{{ item.name }}</div>
              <div class="description">{{ item.description }}</div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="search">
        <base-input
          v-model="inputSearch"
          background="#fff"
          color="#000"
          :placeholder="t('common.labels.search')"
          @key:enter="search()"
        >
          <template #sufix>
            <div class="icon">
              <svg-icon icon="search" size="1.75rem" @click="search()" />
            </div>
          </template>
        </base-input>
      </div>

      <div class="list">
        <template v-for="(item, index) in methods" :key="index">
          <div
            class="item"
            @click="selectMethod(item)"
            :class="{
              active: selectedMethod?.method_id === item.method_id,
              disabled: props.ids?.includes(item.method_id),
            }"
          >
            <div class="name">
              {{ item.code }}
            </div>
            <div class="type">
              {{ item.name }}
            </div>
          </div>
        </template>
        <template v-if="methods?.length === 0">
          <div class="empty">
            {{ t("common.texts.no_results") }}
          </div>
        </template>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.base-search {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .group {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 250px;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;
  }

  .list {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 50dvh;
    overflow-y: scroll;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;

    .item {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      border: 1px solid var(--border);
      padding-block: 0.5rem;
      padding-inline: 1rem;
      border-radius: 0.5rem;

      &.active {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--primary-color);
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .name {
        font-weight: bold;
      }
    }
  }
}
</style>
